/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { fetchWithAuth, isAuthenticated } from "../auth/auth";
import { getApiUrl } from "../utils/getApiUrl";
import type { LanguageModel } from "../type/language";

export type LanguageContextType = {
  languages: LanguageModel[];
  selectedLanguageId: number | undefined;
  setSelectedLanguageId: (id: number) => void;
  loaded: boolean;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return ctx;
}

export const LANGUAGE_STORAGE_KEY = "app_language_id";
export const LANGUAGE_CHANGED_EVENT = "language:changed";

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [languages, setLanguages] = useState<LanguageModel[]>([]);
  const [selectedLanguageId, setSelectedLanguageIdState] = useState<number | undefined>(() => {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored ? Number(stored) : undefined;
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) return;

    fetchWithAuth(getApiUrl("dictionaries/languages"))
      .then((res) => (res.ok ? res.json() : []))
      .then((data: LanguageModel[]) => {
        setLanguages(data);

        const storedId = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        const storedIdNum = storedId ? Number(storedId) : undefined;
        const isStoredValid = storedIdNum !== undefined && data.some((l) => l.idlanguage === storedIdNum);

        if (isStoredValid) {
          setSelectedLanguageIdState(storedIdNum);
        } else {
          const defaultLanguage = data.find((l) => l.is_default) || data[0];
          if (defaultLanguage) {
            setSelectedLanguageIdState(defaultLanguage.idlanguage);
            localStorage.setItem(LANGUAGE_STORAGE_KEY, String(defaultLanguage.idlanguage));
          }
        }
      })
      .catch((err) => console.error("Failed to load languages", err))
      .finally(() => setLoaded(true));
  }, []);

  const setSelectedLanguageId = useCallback((id: number) => {
    setSelectedLanguageIdState(id);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, String(id));
    document.dispatchEvent(new CustomEvent(LANGUAGE_CHANGED_EVENT, { detail: id }));
  }, []);

  return (
    <LanguageContext.Provider value={{ languages, selectedLanguageId, setSelectedLanguageId, loaded }}>
      {children}
    </LanguageContext.Provider>
  );
};
